import { Alert, Button, Card, Form, Input, InputNumber, Space, message } from 'antd';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Template = { id: string; code: string; name: string; sequence_no: number; description?: string | null };
type Values = { code: string; name: string; sequence: number; description?: string };

export default function HpcTemplateFormPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Values>();
  const permissions = useAuthStore((s) => s.user?.permissions || []);
  const canManage = permissions.includes('education.hpc.manage') || permissions.includes('education.admin');
  const templates = useQuery({
    queryKey: ['education-hpc-templates'],
    enabled: !isNew,
    queryFn: async () => (await api.get<Template[]>('/education/hpc/templates')).data || [],
  });
  const template = templates.data?.find((row) => row.id === id);
  useEffect(() => {
    if (template) form.setFieldsValue({
      code: template.code,
      name: template.name,
      sequence: template.sequence_no,
      description: template.description || undefined,
    });
    else if (isNew) form.setFieldsValue({ sequence: 1 });
  }, [form, isNew, template]);
  const save = useMutation({
    mutationFn: (values: Values) => isNew
      ? api.post('/education/hpc/templates', values)
      : api.put(`/education/hpc/templates/${id}`, values),
    onSuccess: () => {
      message.success(isNew ? 'Stage template created' : 'Stage template updated');
      queryClient.invalidateQueries({ queryKey: ['education-hpc-templates'] });
      navigate('/education/hpc');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || 'Unable to save stage template'),
  });

  return (
    <EducationPageChrome
      title={isNew ? 'New HPC stage template' : 'Edit HPC stage template'}
      description="Define a school-specific stage label and ordering. This does not define grades."
      extra={<Button onClick={() => navigate('/education/hpc')}>Back to HPC</Button>}
    >
      {!canManage ? <Alert type="warning" showIcon message="HPC manage permission is required." /> : null}
      <Card size="small" style={{ maxWidth: 760 }} loading={!isNew && templates.isLoading}>
        <Form form={form} layout="vertical" disabled={!canManage} onFinish={(values) => save.mutate(values)}>
          <Form.Item name="code" label="Code" rules={[{ required: true, max: 50 }]}>
            <Input maxLength={50} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true, max: 200 }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="sequence" label="Sequence" rules={[{ required: true }]}>
            <InputNumber min={1} precision={0} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={4} maxLength={2000} showCount />
          </Form.Item>
          {canManage ? <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>Save</Button>
            <Button onClick={() => navigate('/education/hpc')}>Cancel</Button>
          </Space> : null}
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
